import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { NotificationTemplateConfiguration, NotificationTemplateType, } from "~/lib/drizzle/type";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";

export const useGetNotificationTemplates = (): UseQueryResult<NotificationTemplateConfiguration[]> => {
    const { getToken } = useAuth();

    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<NotificationTemplateConfiguration[]>({
            url: `${apiBaseUrl}/notification-templates`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getNotificationTemplates'],
        queryFn,
        staleTime: 1000,
        refetchOnWindowFocus: false,
    });
}

export const useUpsertNotificationTemplate = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const mutationFn = async (payload: UpsertNotificationTemplatePayload): Promise<NotificationTemplateConfiguration | null> => {
        const token = await getToken();
        const result = await fetchWrapper<NotificationTemplateConfiguration>({
            url: `${apiBaseUrl}/notification-templates`,
            method: 'PUT',
            token,
            body: payload
        });

        return result
    }

    return useMutation(mutationFn, {
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({ queryKey: ['getNotificationTemplates'] });
            onSuccess && onSuccess(data);
        },
        onError
    });
}

interface UpsertNotificationTemplatePayload {
    type: NotificationTemplateType;
    content: string;
    mapping: Record<string, string>;
}
